import { isPast } from 'date-fns/isPast';
import { isThisMonth } from 'date-fns/isThisMonth';
import { useCallback, useEffect, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import usePaymentLimits from '../../../fetchHooks/usePaymentLimits';
import type { PaymentLimits } from '../../../generated-types';
import { OutboxWrapper } from '../../../models/outbox';

const getOvershootingAmount = (selectedItems: OutboxWrapper[], paymentLimits?: PaymentLimits) => {
    if (!paymentLimits) return 0;
    const amount =
        selectedItems
            .filter((item) => {
                const date = getZonedDate(item.dueDate);
                return isThisMonth(date) || isPast(date);
            })
            .reduce((amount, outboxWrapper) => amount + outboxWrapper.amount, 0) ?? 0;

    return (
        amount + (paymentLimits.accumulatedMonthlyTransactionAmount ?? 0) - paymentLimits.maxMonthlyTransactionAmount
    );
};

export default (data: OutboxWrapper[]) => {
    const [selectedItems, setSelectedItems] = useState<OutboxWrapper[]>(
        data.filter((x) => x.canBeSelected && !x.probablyPSD2Payment),
    );
    const { data: paymentLimits } = usePaymentLimits();
    const [overshootingAmount, setOvershootingAmount] = useState(0);
    const errorState = useState<string>();
    const [, setErrorMessage] = errorState;

    useEffect(() => {
        const selectedItemIds: string[] = selectedItems.map((item) => item.uniqueId.value);
        const idsList = data.map((item) => item.uniqueId.value).filter((id) => selectedItemIds.includes(id));
        const updatedSelectedItems = idsList
            .map((id) => data.find((item) => item.uniqueId.value === id))
            .filter((i) => !!i) as OutboxWrapper[];
        setSelectedItems(updatedSelectedItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.length]);

    useEffect(() => {
        const items = selectedItems
            .map((item) => item.uniqueId)
            .map((id) => data.find((el) => el.uniqueId.value === id.value))
            .filter((el) => !!el) as OutboxWrapper[];
        const overshootingAmount = getOvershootingAmount(items, paymentLimits);
        setOvershootingAmount(overshootingAmount);
    }, [data, paymentLimits, selectedItems]);

    const switchListItem = useCallback(
        (itemId: string) => () => {
            const selectedItemIds: string[] = selectedItems.map((item) => item.uniqueId.value);
            const index = selectedItemIds.indexOf(itemId);
            const newIds =
                index === -1
                    ? [...selectedItemIds, itemId]
                    : [...selectedItemIds.slice(0, index), ...selectedItemIds.slice(index + 1)];
            const updatedSelectedItems = newIds
                .map((id) => data.find((item) => item.uniqueId.value === id))
                .filter((i) => !!i) as OutboxWrapper[];
            setSelectedItems(updatedSelectedItems);
        },
        [data, selectedItems],
    );

    const setSelectedItem = useCallback(
        (itemId: string) => {
            const updatedSelectedItems = [itemId]
                .map((id) => data.find((item) => item.uniqueId.value === id))
                .filter((i) => !!i) as OutboxWrapper[];
            setSelectedItems(updatedSelectedItems);
        },
        [data],
    );

    useEffect(() => {
        setErrorMessage(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItems, setErrorMessage]);

    const toggleAllSelected = useCallback(() => {
        if (selectedItems.length > 0) {
            setSelectedItems([]);
        } else {
            setSelectedItems(data.filter((x) => x.canBeSelected));
        }
    }, [data, selectedItems, setSelectedItems]);

    return {
        selectedItems,
        switchListItem,
        overshootingAmount,
        setSelectedItem,
        errorState,
        toggleAllSelected,
    };
};
